
import { useState } from 'react';
import LogoUploader from '../../../components/base/LogoUploader';

const GoogleSeoPage = () => {
  // Logo state
  const [logo, setLogo] = useState('');
  const [showLogoUploader, setShowLogoUploader] = useState(false);

  const handleLogoChange = (logoUrl: string) => {
    setLogo(logoUrl);
    setShowLogoUploader(false);
    // Сохраняем логотип в localStorage для сохранения между сессиями
    if (logoUrl) {
      localStorage.setItem('siteLogo', logoUrl);
    } else {
      localStorage.removeItem('siteLogo');
    }
  };

  // Загружаем логотип при инициализации
  useState(() => {
    const savedLogo = localStorage.getItem('siteLogo');
    if (savedLogo) {
      setLogo(savedLogo);
    }
  });

  // Original form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/google-seo-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          website: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      title: 'Core Web Vitals',
      description: 'Оптимизация показателей скорости и пользовательского опыта',
      icon: 'ri-speed-line'
    },
    {
      title: 'E-A-T оптимизация',
      description: 'Повышение экспертности, авторитетности и доверия к сайту',
      icon: 'ri-shield-star-line'
    },
    {
      title: 'Google Search Console',
      description: 'Полная настройка и мониторинг через инструменты Google',
      icon: 'ri-search-line'
    },
    {
      title: 'Международные стандарты',
      description: 'Соответствие глобальным требованиям поисковой оптимизации',
      icon: 'ri-global-line'
    },
    {
      title: 'Структурированные данные',
      description: 'Разметка Schema.org для расширенных сниппетов',
      icon: 'ri-code-s-slash-line'
    },
    {
      title: 'Мобильная оптимизация',
      description: 'Mobile-first индексация и адаптивный дизайн',
      icon: 'ri-smartphone-line'
    }
  ];

  const advantages = [
    {
      title: 'Сертифицированные специалисты',
      description: 'Наша команда имеет официальные сертификаты Google по цифровому маркетингу',
      icon: 'ri-award-line'
    },
    {
      title: 'Актуальные алгоритмы',
      description: 'Постоянно отслеживаем обновления Google и адаптируем стратегии',
      icon: 'ri-refresh-line'
    },
    {
      title: 'Международный опыт',
      description: 'Успешно продвигаем сайты как на российском, так и на зарубежных рынках',
      icon: 'ri-earth-line'
    },
    {
      title: 'Белые методы',
      description: 'Используем только официально рекомендованные Google практики',
      icon: 'ri-shield-check-line'
    }
  ];

  const results = [
    {
      metric: '+280%',
      description: 'Рост органического трафика',
      icon: 'ri-arrow-up-line'
    },
    {
      metric: '92%',
      description: 'Запросов в ТОП-10',
      icon: 'ri-trophy-line'
    },
    {
      metric: '3-6 месяцев',
      description: 'До стабильных результатов',
      icon: 'ri-time-line'
    },
    {
      metric: '100%',
      description: 'Соответствие Google Guidelines',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Логотип" 
                  className="h-10 w-auto object-contain cursor-pointer"
                  onClick={() => setShowLogoUploader(true)}
                />
              ) : (
                <div 
                  onClick={() => setShowLogoUploader(true)}
                  className="text-2xl font-bold cursor-pointer hover:text-blue-600 transition-colors"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  logo
                </div>
              )}
              <button
                onClick={() => setShowLogoUploader(true)}
                className="text-sm text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
                title="Изменить логотип"
              >
                <i className="ri-edit-line"></i>
              </button>
            </div>
            
            <div className="flex items-center space-x-6">
              <nav className="hidden md:flex space-x-6">
                <a href="/#services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                  Услуги
                </a>
                <a href="/#tariffs" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                  Тарифы
                </a>
                <a href="/#cases" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                  Кейсы
                </a>
                <a href="/blog" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                  Блог
                </a>
              </nav>
              <button
                onClick={() => document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Заказать продвижение
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Загрузить логотип</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <LogoUploader 
              onLogoChange={handleLogoChange}
              currentLogo={logo}
            />
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowLogoUploader(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(66, 133, 244, 0.1), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=Google%20SEO%20optimization%20workspace%20with%20Google%20Search%20Console%20interface%2C%20Core%20Web%20Vitals%20metrics%2C%20international%20SEO%20tools%2C%20blue%20Google%20branding%20elements%2C%20professional%20digital%20marketing%20environment&width=1920&height=1080&seq=google-seo-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Продвижение в<br />
            <span className="text-blue-400">Google</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Профессиональное SEO продвижение в Google с соблюдением международных стандартов и лучших практик
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Заказать продвижение
            </button>
            <button 
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Наши методы
            </button>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Результаты продвижения</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Эффективность нашего подхода к продвижению в Google
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6 mx-auto">
                  <i className={`${result.icon} text-2xl text-blue-600`}></i>
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{result.metric}</div>
                <p className="text-gray-600">{result.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Что включает продвижение</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Современные методы оптимизации для Google
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Наши преимущества</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Почему стоит выбрать нас для продвижения в Google
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className={`${advantage.icon} text-2xl text-blue-600`}></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{advantage.title}</h3>
                  <p className="text-gray-600">{advantage.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Web Vitals Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Core Web Vitals — ключ к успеху в Google
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Оптимизируем критически важные показатели пользовательского опыта, которые напрямую влияют на ранжирование
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">LCP (Largest Contentful Paint) &lt; 2.5s</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">FID (First Input Delay) &lt; 100ms</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">CLS (Cumulative Layout Shift) &lt; 0.1</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-blue-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Mobile-first индексация</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Core%20Web%20Vitals%20dashboard%20showing%20perfect%20scores%20for%20LCP%20FID%20CLS%20metrics%2C%20Google%20PageSpeed%20Insights%20interface%20with%20green%20performance%20indicators%2C%20mobile%20optimization%20results%2C%20professional%20web%20development%20workspace&width=600&height=400&seq=core-web-vitals&orientation=landscape"
                alt="Core Web Vitals"
                className="w-full rounded-xl shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Order Form Section */}
      <section id="order-form" className="py-20 bg-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Заказать продвижение в Google</h2>
              <p className="text-xl text-gray-600">
                Получите стабильный рост позиций с соблюдением всех требований Google
              </p>
            </div>
            
            <form 
              id="google-seo-order"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Введите ваше имя"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-semibold text-gray-700 mb-2">
                    Сайт для продвижения *
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="https://yoursite.com"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Целевые запросы и пожелания
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Укажите ключевые запросы, целевые рынки (Россия/международные), особенности бизнеса..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Отправка...' : 'Заказать продвижение в Google'}
              </button>
              
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Спасибо! Ваша заявка принята. Мы свяжемся с вами в течение часа для обсуждения стратегии продвижения.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Произошла ошибка при отправке. Пожалуйста, попробуйте еще раз.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {logo ? (
                <img 
                  src={logo} 
                  alt="Логотип" 
                  className="h-12 w-auto object-contain mb-4"
                />
              ) : (
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                  logo
                </h3>
              )}
              <p className="text-gray-400 mb-4">
                Профессиональное SEO продвижение для вашего бизнеса
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Услуги</h4>
              <ul className="space-y-2">
                <li><a href="/services/seo-audit" className="text-gray-400 hover:text-white transition-colors cursor-pointer">SEO Аудит</a></li>
                <li><a href="/services/yandex-seo" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Продвижение в Яндексе</a></li>
                <li><a href="/services/google-seo" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Продвижение в Google</a></li>
                <li><a href="/services/google-maps" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Google Карты</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Компания</h4>
              <ul className="space-y-2">
                <li><a href="/#cases" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Кейсы</a></li>
                <li><a href="/#tariffs" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Тарифы</a></li>
                <li><a href="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Блог</a></li>
                <li><a href="/#consultation-form" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Контакты</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Контакты</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  +7 (950) 360-35-20
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@seo-vektor.ru
                </li>
                <li className="flex items-center">
                  <i className="ri-time-line mr-2"></i>
                  Пн-Пт: 9:00-18:00
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 SEO Продвижение. Все права защищены. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer ml-1">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GoogleSeoPage;
